import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// A sun-tracking solar array: a large photovoltaic panel on a torque tube atop a
// central pylon, that slowly rocks to follow the sun. All the moving geometry is
// named "array" and rotates about the tube axis (the hub) in the tracking
// animation.
const CELL = "#244a8c";
const GRID = "#3a64a8";
const FRAME = "#c2c6cd";
const TUBE = "#6c727a";
const PYLON = "#878e96";

const HUB_Y = 1.0;
const BASE_TILT = 0.32;

// A 4x2 cell panel built coplanar on a plane pre-tilted by BASE_TILT about X,
// centred on the hub, every part named "array".
function array(): Part[] {
  const c = Math.cos(BASE_TILT), s = Math.sin(BASE_TILT);
  const cy = HUB_Y, cz = 0;
  const P = (lx: number, ly: number, lz: number): Vec3 => [lx, cy + ly * c - lz * s, cz + ly * s + lz * c];
  const rot: Vec3 = [BASE_TILT, 0, 0];
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(1.3, 0.04, 0.7), color: FRAME, position: P(0, 0, 0), rotation: rot, name: "array" },
  ];
  // 4x2 grid of cells.
  for (let i = 0; i < 4; i++) for (let j = 0; j < 2; j++) {
    const lx = -0.49 + i * 0.32, lz = -0.18 + j * 0.36;
    parts.push({ geometry: new THREE.BoxGeometry(0.3, 0.03, 0.34), color: CELL, position: P(lx, 0.02, lz), rotation: rot, name: "array" });
  }
  // Seam lines.
  for (const lz of [-0.18, 0.18]) parts.push({ geometry: new THREE.BoxGeometry(1.26, 0.035, 0.012), color: GRID, position: P(0, 0.03, lz), rotation: rot, name: "array" });
  for (let i = 0; i < 3; i++) parts.push({ geometry: new THREE.BoxGeometry(0.012, 0.035, 0.66), color: GRID, position: P(-0.33 + i * 0.33, 0.03, 0), rotation: rot, name: "array" });
  // Torque tube under the panel (also moves with the array).
  parts.push({ geometry: new THREE.CylinderGeometry(0.04, 0.04, 1.3, 10), color: TUBE, position: [0, HUB_Y - 0.06, 0], rotation: [0, 0, Math.PI / 2], name: "array" });
  return parts;
}

export const solarTracker = defineModel({
  id: "solar-tracker",
  batchId: "electric",
  name: "Solar tracker",
  tags: ["building", "electric", "power", "energy", "solar", "renewable", "animated"],
  summary: "A pylon-mounted PV array that rocks to track the sun.",
  footprint: 0.8,
  parts(): Part[] {
    return [
      { geometry: new THREE.BoxGeometry(0.5, 0.12, 0.5), color: "#6f6f74", position: [0, 0.06, 0] },
      { geometry: new THREE.CylinderGeometry(0.07, 0.1, 0.9, 10), color: PYLON, position: [0, 0.55, 0] },
      { geometry: new THREE.BoxGeometry(0.16, 0.16, 0.16), color: TUBE, position: [0, HUB_Y - 0.06, 0] },
      ...array(),
    ];
  },
  animations: [
    {
      id: "track",
      label: "Tracking the sun",
      duration: 8,
      loop: true,
      tracks: [
        {
          target: "array",
          pivot: [0, HUB_Y, 0],
          rotation: [
            { t: 0, value: [-0.3, 0, 0], ease: "sine" },
            { t: 4, value: [0.3, 0, 0], ease: "sine" },
            { t: 8, value: [-0.3, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
